import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Output,
    signal,
    ViewEncapsulation,
    WritableSignal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import {
    DigitaiFleet,
    DigitaiFleetAircraft,
    DigitaiFleetAircraftEngine,
    DigitaiFleetAircraftEngineStatus,
} from '@apps/digitai';
import { FleetAircraftEngineService } from '@apps/digitai/fleet-aircraft-engine';
import {
    Action,
    defaultDetailImports,
    log,
    mapActions,
    SnackBarInvalidFormComponent,
    uuid,
    ViewDetailComponent,
} from '@aurora';
import { lastValueFrom, takeUntil } from 'rxjs';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';

@Component({
    selector: 'digitai-fleet-aircraft-engine-detail-dialog',
    templateUrl: './fleet-aircraft-engine-detail-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ...defaultDetailImports,
        MatDialogModule, MatSelectModule, MtxDatetimepickerModule
    ],
})
export class FleetAircraftEngineDetailDialogComponent extends ViewDetailComponent {
    // ---- customizations ----
    actionScope = 'digitai::fleetAircraftEngine.detailDialog';
    fleet: DigitaiFleet;
    fleetAircraft: DigitaiFleetAircraft;
    totalMotors: number[];
    fleetAircraftAssembledPositionMotors: WritableSignal<Set<number>> = signal(
        new Set([])
    );
    currentPositionMotor: number;

    // outputs
    @Output() created = new EventEmitter<{
        currentPositionMotor: number;
        fleetAircraftEngine: DigitaiFleetAircraftEngine;
    }>();
    @Output() updated = new EventEmitter<{
        currentPositionMotor: number;
        fleetAircraftEngine: DigitaiFleetAircraftEngine;
    }>();

    // Object retrieved from the database request,
    // it should only be used to obtain uninitialized
    // data in the form, such as relations, etc.
    // It should not be used habitually, since the source of truth is the form.
    managedObject: DigitaiFleetAircraftEngine;

    constructor(
        public readonly dialogRef: MatDialogRef<FleetAircraftEngineDetailDialogComponent>,
        private readonly fleetAircraftEngineService: FleetAircraftEngineService
    ) {
        super();
    }

    // this method will be called after the ngOnInit of
    // the parent class you can use instead of ngOnInit
    init(): void {
        /**/
    }

    onSubmit($event): void {
        // we have two nested forms, we check that the submit comes from the button
        // that corresponds to the main form to the main form
        if (
            $event.submitter.getAttribute('form') !==
            $event.submitter.form.getAttribute('id')
        ) {
            $event.preventDefault();
            $event.stopPropagation();
            return;
        }

        // manage validations before execute actions
        if (this.fg.invalid) {
            log('[DEBUG] Error to validate form: ', this.fg);
            this.validationMessagesService.validate();

            this.snackBar.openFromComponent(SnackBarInvalidFormComponent, {
                data: {
                    message: `${this.translocoService.translate('InvalidForm')}`,
                    textButton: `${this.translocoService.translate('InvalidFormOk')}`,
                },
                panelClass: 'error-snackbar',
                verticalPosition: 'top',
                duration: 10000,
            });
            return;
        }

        this.actionService.action({
            id: mapActions(this.currentViewAction.id, {
                'digitai::fleetAircraftEngine.detailDialog.new':
                    'digitai::fleetAircraftEngine.detailDialog.create',
                'digitai::fleetAircraftEngine.detailDialog.pool':
                    'digitai::fleetAircraftEngine.detailDialog.create',
                'digitai::fleetAircraftEngine.detailDialog.edit':
                    'digitai::fleetAircraftEngine.detailDialog.update',
                'digitai::fleetAircraftEngine.detailDialog.link':
                    'digitai::fleetAircraftEngine.detailDialog.update',
            }),
            isViewAction: false,
        });
    }

    createForm(): void {
        /* eslint-disable key-spacing */
        this.fg = this.fb.group({
            id: [
                '',
                [
                    Validators.required,
                    Validators.minLength(36),
                    Validators.maxLength(36),
                ],
            ],
            tenantIds: [[], [Validators.required]],
            fleetId: ['', [Validators.required, Validators.maxLength(36)]],
            status: ['', [Validators.required]],
            fleetAircraftId: [
                null,
                [
                    Validators.required,
                    Validators.minLength(36),
                    Validators.maxLength(36),
                ],
            ],
            position: [null, [Validators.required]],
            serialNumber: [
                '',
                [Validators.required, Validators.maxLength(128)],
            ],
            engineFlyingHours: [null, [Validators.min(0)]],
            operationDate: [null, [Validators.required]],
        });
        /* eslint-enable key-spacing */
    }

    async handleAction(action: Action): Promise<void>
    {
        // add optional chaining (?.) to avoid first call where behaviour subject is undefined
        switch (action?.id) {
            /* #region common actions */
            case 'digitai::fleetAircraftEngine.detailDialog.new':
                this.fleet = action.meta.fleet;
                this.fleetAircraft = action.meta.fleetAircraft;
                this.fleetAircraftAssembledPositionMotors =
                    action.meta.fleetAircraftAssembledPositionMotors;

                this.fg.get('id').setValue(uuid());
                this.fg.get('tenantIds').setValue(this.fleet.tenantIds);
                this.fg.get('fleetId').setValue(this.fleet.id);
                this.fg
                    .get('status')
                    .setValue(DigitaiFleetAircraftEngineStatus.INSTALL);
                this.fg.get('fleetAircraftId').setValue(this.fleetAircraft.id);

                // create array with numbers from 1 to totalMotors
                this.totalMotors = Array.from(
                    { length: this.fleet.aircraftModel.totalMotors },
                    (_, i) => i + 1
                );
                break;
            case 'digitai::fleetAircraftEngine.detailDialog.pool':
                this.fleet = action.meta.fleet;

                this.fg.get('id').setValue(uuid());
                this.fg.get('tenantIds').setValue(this.fleet.tenantIds);
                this.fg.get('fleetId').setValue(this.fleet.id);
                this.fg
                    .get('status')
                    .setValue(DigitaiFleetAircraftEngineStatus.POOL);

                // Remove required in form fields
                this.fg.get('fleetAircraftId').clearValidators();
                this.fg.get('position').clearValidators();
                this.fg.get('fleetAircraftId').updateValueAndValidity();
                this.fg.get('position').updateValueAndValidity();

                // remove required from operationDate
                this.fg.get('operationDate').clearValidators();
                this.fg.get('operationDate').updateValueAndValidity();
                this.fg.get('operationDate').setErrors(null)
                break;

            case 'digitai::fleetAircraftEngine.detailDialog.edit':
                this.fleetAircraftEngineService.fleetAircraftEngine$
                    .pipe(takeUntil(this.unsubscribeAll$))
                    .subscribe((item) => {
                        this.fleet = action.meta.fleet;
                        this.fleetAircraft = action.meta.fleetAircraft;
                        this.fleetAircraftAssembledPositionMotors =
                            action.meta.fleetAircraftAssembledPositionMotors;

                        // create array with numbers from 1 to totalMotors
                        this.totalMotors = Array.from(
                            { length: this.fleet.aircraftModel.totalMotors },
                            (_, i) => i + 1
                        );

                        this.managedObject = item;
                        this.currentPositionMotor = item.position;
                        this.fg.patchValue(item);
                    });
                break;

            case 'digitai::fleetAircraftEngine.detailDialog.create':
                try {
                    await lastValueFrom(
                        this.fleetAircraftEngineService.create<DigitaiFleetAircraftEngine>(
                            {
                                object: this.fg.value,
                            }
                        )
                    );

                    this.created.emit({
                        currentPositionMotor: this.currentPositionMotor,
                        fleetAircraftEngine: this.fg.value,
                    });

                    this.snackBar.open(
                        `${this.translocoService.translate('digitai.FleetAircraftEngine')} ${this.translocoService.translate('Created.M')}`,
                        undefined,
                        {
                            verticalPosition: 'top',
                            duration: 3000,
                        }
                    );

                    this.dialogRef.close();
                } catch (error) {
                    log(`[DEBUG] Catch error in ${action.id} action: ${error}`);
                }
                break;

            case 'digitai::fleetAircraftEngine.detailDialog.update':
                try {
                    await lastValueFrom(
                        this.fleetAircraftEngineService.updateById<DigitaiFleetAircraftEngine>(
                            {
                                object: this.fg.value,
                            }
                        )
                    );

                    this.updated.emit({
                        currentPositionMotor: this.currentPositionMotor,
                        fleetAircraftEngine: this.fg.value,
                    });

                    this.snackBar.open(
                        `${this.translocoService.translate('digitai.FleetAircraftEngine')} ${this.translocoService.translate('Saved.M')}`,
                        undefined,
                        {
                            verticalPosition: 'top',
                            duration: 3000,
                        }
                    );

                    this.dialogRef.close();
                } catch (error) {
                    log(`[DEBUG] Catch error in ${action.id} action: ${error}`);
                }
                break;
            /* #endregion common actions */

            /* #region linked actions */
            case 'digitai::fleetAircraftEngine.detailDialog.link':
                this.fleetAircraftEngineService.fleetAircraftEngine$
                    .pipe(takeUntil(this.unsubscribeAll$))
                    .subscribe((item) => {
                        this.fleet = action.meta.fleet;
                        this.fleetAircraft = action.meta.fleetAircraft;
                        this.fleetAircraftAssembledPositionMotors =
                            action.meta.fleetAircraftAssembledPositionMotors;

                        // create array with numbers from 1 to totalMotors
                        this.totalMotors = Array.from(
                            { length: this.fleet.aircraftModel.totalMotors },
                            (_, i) => i + 1
                        );

                        this.managedObject = item;
                        this.fg.patchValue(item);

                        // add values from fleetAircraft
                        this.fg
                            .get('status')
                            .setValue(DigitaiFleetAircraftEngineStatus.INSTALL);
                        this.fg
                            .get('fleetAircraftId')
                            .setValue(this.fleetAircraft.id);
                        this.fg.get('serialNumber').disable();
                    });
                break;
            /* #endregion linked actions */
        }
    }
}
